/**
 * Responsive charge-history rows, ported from the prototype `BillingTab` —
 * a semantic table at ≥768px (in its own horizontal-scroll container, the
 * sibling v2-table convention), stacked labeled cards below (six columns don't
 * survive 375px; mirrors the AmendmentsTab pattern). `rows` is label/value
 * pairs in column order, uniform across rows.
 *
 * `boundary` is the §6.6 funding-switch marker: rendered before the row keyed
 * `beforeKey` (in newest-first display order, the first pre-switch credits
 * row, which sits below the post-switch USD rows), making the credits↔USD
 * transition legible in both layouts. Only the switched path passes it.
 */
import { Fragment } from 'react';

export interface ChargeRow {
  key: string;
  cells: [string, React.ReactNode][];
}

export interface ChargeBoundary {
  beforeKey: string;
  label: string;
}

interface ChargeHistoryProps {
  rows: ChargeRow[];
  boundary?: ChargeBoundary;
}

export default function ChargeHistory({ rows, boundary }: ChargeHistoryProps): JSX.Element | null {
  if (rows.length === 0) return null;
  const headers = rows[0].cells.map(([label]) => label);
  const isBoundaryRow = (key: string) => boundary?.beforeKey === key;
  return (
    <>
      {/* The switched table is five columns of pills and multi-line captions;
          at exactly 768px it can outgrow its container. Same wrapper the
          sibling v2 tables use (HoursTab, FulfillmentPassiveFeed, …). */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-surface-border">
              {headers.map((h) => (
                <th key={h} className="px-12 py-8 text-on-surface-muted subtitle-2">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.key}>
                {isBoundaryRow(r.key) ? (
                  <tr className="border-b border-surface-border bg-surface-elevated">
                    <td
                      colSpan={headers.length}
                      className="px-12 py-8 text-on-surface-muted subtitle-2"
                    >
                      {boundary?.label}
                    </td>
                  </tr>
                ) : null}
                <tr className="border-b border-surface-border align-top">
                  {r.cells.map(([label, value]) => (
                    <td key={label} className="px-12 py-12 text-on-surface body-2">
                      {value}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="flex flex-col gap-16 md:hidden">
        {rows.map((r) => (
          <Fragment key={r.key}>
            {isBoundaryRow(r.key) ? (
              <li className="rounded-md bg-surface-elevated px-12 py-8 text-on-surface-muted subtitle-2">
                {boundary?.label}
              </li>
            ) : null}
            <li className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated p-16">
              {r.cells.map(([label, value]) => (
                <div key={label} className="flex flex-col gap-2">
                  <span className="text-on-surface-muted subtitle-2">{label}</span>
                  <span className="text-on-surface body-2">{value}</span>
                </div>
              ))}
            </li>
          </Fragment>
        ))}
      </ul>
    </>
  );
}
